import { CSSProperties, useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import cn from "classnames";

/**
 * Flag highlights on a photo or PDF page can sit on top of each other (two
 * findings in the same line, a visual region over a quoted caption), and the
 * top one swallows the hover. Each box carries an eye toggle: hiding it drops
 * the tint and leaves a dashed outline whose interior lets the pointer
 * through to whatever is underneath. Hovering the outline itself brings the
 * eye back to restore it. View state only — the lead is never touched.
 */
export function useHiddenFlags() {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const hide = (id: string) => setHidden((prev) => new Set(prev).add(id));
  const restore = (id: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  const reset = () => setHidden(new Set());
  return { hidden, hide, restore, reset };
}

const cornerButton =
  "absolute -top-2.5 -right-2.5 size-5 rounded-full flex items-center justify-center pointer-events-auto " +
  "bg-base-100 text-base-content/70 border border-base-300 shadow-sm " +
  "opacity-0 group-hover:opacity-100 hover:text-base-content hover:bg-base-200 transition-opacity cursor-pointer";

// Hover strips along each edge of a hidden box: the outline is the only
// part that catches the pointer, so a box below stays reachable.
const EDGES: CSSProperties[] = [
  { top: -3, left: 0, right: 0, height: 7 },
  { bottom: -3, left: 0, right: 0, height: 7 },
  { left: -3, top: 0, bottom: 0, width: 7 },
  { right: -3, top: 0, bottom: 0, width: 7 },
];

/**
 * One highlight box. `blend` multiplies the tint into the page (PDF paper)
 * so the text under it stays legible; photos use a plain translucent fill
 * with a ring instead. The tint and the buttons are siblings so the buttons
 * never inherit the blend or the colour. `control` is true for the first
 * box of a lead only, so a multi-line quote shows one toggle.
 */
export const FlagBox: React.FC<{
  style: CSSProperties;
  severity: number;
  active: boolean;
  hidden: boolean;
  blend?: boolean;
  control: boolean;
  onHide: () => void;
  onRestore: () => void;
  onMouseEnter: (el: HTMLElement) => void;
  onMouseLeave: () => void;
}> = ({ style, severity, active, hidden, blend, control, onHide, onRestore, onMouseEnter, onMouseLeave }) => {
  const high = severity >= 4;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  if (hidden) {
    return (
      <div className="group absolute pointer-events-none" style={style}>
        <div
          className={cn(
            "absolute inset-0 rounded-[3px] border border-dashed transition-colors",
            high ? "border-red-400/80 group-hover:border-red-500" : "border-amber-400/80 group-hover:border-amber-500",
            active && (high ? "border-solid border-red-500 bg-red-300/30" : "border-solid border-amber-500 bg-amber-300/30")
          )}
        />
        {EDGES.map((edge, i) => (
          <div
            key={i}
            className="absolute pointer-events-auto cursor-pointer"
            style={edge}
            title="Show highlight"
            onClick={(e) => {
              stop(e);
              onRestore();
            }}
            onMouseDown={stop}
          />
        ))}
        {control && (
          <button
            type="button"
            title="Show highlight"
            className={cornerButton}
            onClick={(e) => {
              stop(e);
              onRestore();
            }}
            onMouseDown={stop}
          >
            <EyeIcon className="size-3" />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="group absolute cursor-help" style={style} onMouseEnter={(e) => onMouseEnter(e.currentTarget)} onMouseLeave={onMouseLeave}>
      <div
        className={cn(
          "absolute inset-0 transition-colors",
          blend
            ? cn(
                "rounded-[2px] mix-blend-multiply",
                high
                  ? active
                    ? "bg-red-400/60 ring-1 ring-red-500"
                    : "bg-red-300/40 group-hover:bg-red-300/60"
                  : active
                    ? "bg-amber-400/60 ring-1 ring-amber-500"
                    : "bg-amber-300/40 group-hover:bg-amber-300/60"
              )
            : cn(
                "rounded-[3px]",
                high
                  ? active
                    ? "bg-red-400/50 ring-2 ring-red-500"
                    : "bg-red-300/35 ring-1 ring-red-400/80 group-hover:bg-red-300/55"
                  : active
                    ? "bg-amber-400/50 ring-2 ring-amber-500"
                    : "bg-amber-300/35 ring-1 ring-amber-400/80 group-hover:bg-amber-300/55"
              )
        )}
      />
      {control && (
        <button
          type="button"
          title="Hide highlight (the flag stays in the list)"
          className={cornerButton}
          onClick={(e) => {
            stop(e);
            onHide();
            onMouseLeave();
          }}
          onMouseDown={stop}
        >
          <EyeSlashIcon className="size-3" />
        </button>
      )}
    </div>
  );
};

export const HiddenFlagsChip: React.FC<{ count: number; onReset: () => void; className?: string }> = ({ count, onReset, className }) =>
  count > 0 ? (
    <button
      type="button"
      onClick={onReset}
      className={cn(
        "absolute bottom-16 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full text-xs font-medium shadow inline-flex items-center gap-1.5",
        "bg-base-100 text-base-content/80 border border-base-300 hover:bg-base-200 transition-colors",
        className
      )}
    >
      <EyeIcon className="size-3.5" /> {count} highlight{count === 1 ? "" : "s"} hidden · Show all
    </button>
  ) : null;
